import {
  autoUpdate,
  FloatingNode,
  FloatingPortal,
  flip,
  offset,
  shift,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFocus,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import React, { useMemo, useRef, useState } from 'react'

import { Label } from '../label'
import { SELECT_OFFSET } from './select.constants'
import { useTreeOptions } from './select.hooks'
import * as S from './select.styled'
import {
  SelectMultiProps,
  SelectOptionValue,
  SelectProps,
  SelectSingleProps,
  SelectTreeCheckboxProps,
  SelectTreeOption,
  SelectTreeProps,
  SelectTreeSingleProps,
  SelectTypeEnum,
} from './select.types'
import {
  filterOptionsTree,
  findOptionsFlat,
  getTreeCheckboxState,
  restoreMissingFromCache,
  sortSelectedFirst,
  sortTreeSelectedFirst,
  toggleTreeOption,
} from './select.utils'
import { SelectDropdown } from './select-dropdown'
import { SelectTrigger } from './select-trigger'

export const Select = <V extends SelectOptionValue>(props: SelectProps<V>) => {
  const {
    options,
    value,
    placeholder = 'Select an option',
    disabled = false,
    error,
    helperText,
    display = 'default',
    label,
    name,
    className,
    size = 'medium',
    type = SelectTypeEnum.enum.single,
    searchable = false,
    searchPlaceholder,
    trigger,
    width,
    dropdownOptions = {},
    placement = 'bottom-start',
    onReset,
    onSearchChange,
    tooltip,
    icon,
    loading = false,
    withReservedFooter = false,
    required,
    hideCaretOnDisable = false,
    readOnly = false,
    labelPosition = 'top',
    onClear,
    sortSelectedFirst: shouldSortSelectedFirst = true,
  } = props
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const nodeId = useFloatingNodeId()

  const isMulti = type === SelectTypeEnum.enum.multi
  const isTreeCheckbox =
    type === SelectTypeEnum.enum.nested && (props as SelectTreeProps<V>).checkbox === true
  const hasCustomTrigger = !!trigger
  const hasError = !!error

  // Note: footer/aria wiring mirrors `TextInput`. The footer is only meaningful
  // for the built-in trigger; a custom trigger owns its own error presentation.
  const message = error ?? helperText
  const helperId = message ? `${name}-message` : undefined
  const shouldShowFooter = !hasCustomTrigger && (withReservedFooter || !!message)

  const treeOptions = options as Array<SelectTreeOption<V>>

  const treeSelectedSet = useMemo(
    () => new Set(isTreeCheckbox ? (value as V[]) : []),
    [isTreeCheckbox, value],
  )

  // Cache every option object ever seen by value. In async mode the options list
  // only contains the current page of results, so selected options can disappear
  // when the query changes. This ref lets us restore them.
  const knownOptionsRef = useRef(new Map<V, SelectTreeOption<V>>())
  const sortValueRef = useRef<V | V[] | null>(value)

  // Note: only tree-checkbox mode has a spec'd "selected first" order - plain nested
  // (single-pick "Move" mode) consumers are unaffected. Frozen to the selection at open
  // time (mirrors sortValueRef's use below for flat selects) so the list doesn't reshuffle
  // while the user is actively checking items in the same session - only the next open re-sorts.
  const sortedTreeOptions = useMemo(() => {
    if (!isTreeCheckbox) return treeOptions
    const effectiveSelected = new Set(isOpen ? (sortValueRef.current as V[]) : (value as V[]))
    return sortTreeSelectedFirst(treeOptions, effectiveSelected)
  }, [treeOptions, isTreeCheckbox, isOpen, value])

  const filteredOptions = useMemo(() => {
    if (onSearchChange) return sortedTreeOptions
    if (!searchQuery.trim()) return sortedTreeOptions
    const query = searchQuery.toLowerCase()
    return filterOptionsTree(sortedTreeOptions, query)
  }, [sortedTreeOptions, searchQuery, onSearchChange])

  const {
    flattenedOptions: rawFlattenedOptions,
    isExpanded,
    toggleExpanded,
  } = useTreeOptions({
    filteredOptions,
    originalOptions: treeOptions,
    searchQuery,
  })

  const flattenedOptions = useMemo(() => {
    for (const opt of treeOptions) {
      knownOptionsRef.current.set(opt.value, opt)
    }
    if (type === SelectTypeEnum.enum.nested) return rawFlattenedOptions
    if (!shouldSortSelectedFirst && !onSearchChange) return rawFlattenedOptions

    const effectiveValue = isOpen ? sortValueRef.current : value

    if (onSearchChange) {
      const presentValues = new Set(rawFlattenedOptions.map(({ option }) => option.value))
      const selectedValues: V[] = isMulti ? (value as V[]) : value != null ? [value as V] : []
      const missingSelected = restoreMissingFromCache(
        selectedValues,
        presentValues,
        knownOptionsRef.current,
      )

      return sortSelectedFirst(
        [...missingSelected, ...rawFlattenedOptions],
        effectiveValue,
        isMulti,
      )
    }

    return sortSelectedFirst(rawFlattenedOptions, effectiveValue, isMulti)
  }, [
    rawFlattenedOptions,
    type,
    value,
    isMulti,
    onSearchChange,
    treeOptions,
    isOpen,
    shouldSortSelectedFirst,
  ])

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(SELECT_OFFSET),
      flip(),
      shift({ padding: SELECT_OFFSET }),
      sizeMiddleware({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: dropdownOptions.width || `${rects.reference.width}px`,
          })
        },
      }),
    ],
    nodeId,
    onOpenChange: (open) => {
      if (open && readOnly) return
      if (open) {
        sortValueRef.current = value
      }
      setIsOpen(open)
      if (!open) {
        setSearchQuery('')
        onSearchChange?.('')
      }
    },
    open: isOpen,
    placement,
    whileElementsMounted: autoUpdate,
  })

  // Note: searchable's trigger is a real `<input>` — it opens on focus, so typing
  // and tabbing in both open it the same way. Clicking it also opens (but never
  // toggles closed), since an already-focused input (e.g. after Escape) gets no
  // new focus event. The non-searchable `<button>` trigger keeps the plain
  // click-to-toggle behavior.
  // Note: keyboardHandlers off for searchable - useClick treats Space/Enter on the reference (the
  // search box around the input) as a button press and preventDefaults Space, which ate every
  // space typed into the search. The input opens on focus/typing instead.
  const click = useClick(context, { keyboardHandlers: !searchable, toggle: !searchable })
  const focus = useFocus(context, { enabled: searchable })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, focus, dismiss, role])

  const isOptionSelected = (optionValue: V): boolean => {
    if (isTreeCheckbox) {
      const option = knownOptionsRef.current.get(optionValue)
      return !!option && getTreeCheckboxState(option, treeSelectedSet) === 'checked'
    }
    if (isMulti) {
      return (value as V[]).includes(optionValue)
    }
    return value === optionValue
  }

  const getSelectedOptions = () => {
    if (isMulti || isTreeCheckbox) {
      return findOptionsFlat(treeOptions, (opt) => (value as V[]).includes(opt.value))
    }
    return findOptionsFlat(treeOptions, (opt) => opt.value === value)
  }

  const selectedOptions = getSelectedOptions()

  const selectedCount = isMulti || isTreeCheckbox ? (value as V[]).length : selectedOptions.length

  const handleBadgeRemove = (optionValue: V) => {
    if (type !== SelectTypeEnum.enum.multi) return
    const { onChange } = props as SelectMultiProps<V>
    const currentValues = value as V[]
    onChange(currentValues.filter((v) => v !== optionValue))
  }

  const handleOptionClick = (optionValue: V, optionDisabled?: boolean) => {
    if (optionDisabled || disabled || readOnly || loading) return

    if (type === SelectTypeEnum.enum.single || type === SelectTypeEnum.enum.nested) {
      const { onChange } = props as SelectSingleProps<V> | SelectTreeSingleProps<V>
      setIsOpen(false)
      onChange(optionValue)
      return
    }

    const { onChange } = props as SelectMultiProps<V>
    const currentValues = value as V[]
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter((v) => v !== optionValue)
      : [...currentValues, optionValue]
    onChange(newValues)
  }

  // Note: only reached in tree-checkbox mode - a leaf's row click and every
  // option's checkbox click both funnel here (see SelectOptionItem), never
  // `handleOptionClick`, which stays the single-nested/"Move" code path.
  const handleTreeCheckboxToggle = (option: SelectTreeOption<V>) => {
    if (disabled || loading) return
    const { onChange } = props as SelectTreeCheckboxProps<V>
    onChange(toggleTreeOption(option, value as V[]))
  }

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    // Note: typing into a still-focused, closed input (e.g. after Escape) reopens it.
    setIsOpen(true)
    setSearchQuery(query)
    onSearchChange?.(query)
  }

  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const trimmed = e.target.value.trim()
    if (trimmed !== searchQuery) {
      setSearchQuery(trimmed)
      onSearchChange?.(trimmed)
    }
  }

  return (
    <S.Container
      className={className}
      style={width ? { width } : undefined}
      $labelPosition={labelPosition}
      $size={size}
      data-component-id="Select"
    >
      {label && (
        <Label
          tooltip={tooltip}
          htmlFor={name}
          size={size}
          disabled={disabled}
          hasError={hasError}
          required={required}
        >
          {label}
        </Label>
      )}

      <S.Field $size={size}>
        <SelectTrigger
          customTrigger={trigger}
          disabled={disabled}
          error={error}
          hasCustomTrigger={hasCustomTrigger}
          isMulti={isMulti || isTreeCheckbox}
          isOpen={isOpen}
          isTreeCheckbox={isTreeCheckbox}
          multipleSelectedLabel={
            isTreeCheckbox ? (props as SelectTreeCheckboxProps<V>).multipleSelectedLabel : undefined
          }
          placeholder={placeholder}
          referenceProps={getReferenceProps()}
          selectedCount={selectedCount}
          selectedOptions={selectedOptions}
          setReference={refs.setReference}
          size={size}
          icon={icon}
          loading={loading}
          display={display}
          hideCaretOnDisable={hideCaretOnDisable}
          readOnly={readOnly}
          id={name}
          describedBy={helperId}
          searchable={searchable}
          searchQuery={searchQuery}
          searchPlaceholder={searchPlaceholder}
          onBadgeRemove={handleBadgeRemove}
          onClear={onClear}
          onSearchChange={handleSearchInputChange}
          onSearchBlur={handleSearchBlur}
          onToggleOpen={(open) => context.onOpenChange(open)}
        />

        {shouldShowFooter && (
          <S.Footer $size={size}>
            {message && (
              <S.Message $hasError={hasError} id={helperId} role={error ? 'alert' : undefined}>
                {message}
              </S.Message>
            )}
          </S.Footer>
        )}
      </S.Field>

      {isOpen && (
        <FloatingNode id={nodeId}>
          <FloatingPortal>
            <SelectDropdown
              dropdownLabel={dropdownOptions.label}
              dropdownVariant={dropdownOptions.variant}
              flattenedOptions={flattenedOptions}
              floatingProps={getFloatingProps()}
              floatingStyles={floatingStyles}
              getTreeCheckboxState={
                isTreeCheckbox
                  ? (option) => getTreeCheckboxState(option, treeSelectedSet)
                  : undefined
              }
              isExpanded={isExpanded}
              isMulti={isMulti}
              isOptionSelected={isOptionSelected}
              setFloating={refs.setFloating}
              size={size}
              loading={loading}
              onOptionClick={handleOptionClick}
              onTreeCheckboxToggle={isTreeCheckbox ? handleTreeCheckboxToggle : undefined}
              onReset={
                onReset
                  ? () => {
                      onReset()
                      setIsOpen(false)
                      // Note: setIsOpen above is a direct state update, not a floating-ui
                      // interaction (click/dismiss) - it doesn't run the onOpenChange callback
                      // below, so the search state has to be cleared here too.
                      setSearchQuery('')
                      onSearchChange?.('')
                    }
                  : undefined
              }
              onToggleExpanded={toggleExpanded}
              onOptionAction={(action) => {
                // Note: the action usually opens something of its own (a modal) - close the list first
                setIsOpen(false)
                action.onClick()
              }}
            />
          </FloatingPortal>
        </FloatingNode>
      )}
    </S.Container>
  )
}
